/**
 * Resize/compress an image File on the client before upload.
 * - Reads the file via createImageBitmap (handles HEIC on supporting browsers via the platform decoder)
 * - Falls back to Image element if createImageBitmap can't decode (older browsers)
 * - Caps the longest side at maxDim and re-encodes as JPEG with the given quality
 */
export async function compressImage(file, { maxDim = 1920, quality = 0.85 } = {}) {
  // If it's already small and a normal jpeg/png, skip work
  if (file.size < 700 * 1024 && /^image\/(jpe?g|png|webp)$/i.test(file.type)) {
    return file;
  }

  const bitmap = await loadBitmap(file);
  const { width, height } = bitmap;
  const longest = Math.max(width, height);
  const scale = longest > maxDim ? maxDim / longest : 1;
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );
  if (!blob) return file;
  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through */
    }
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}
